import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { ArrowRight, Sparkles, Stethoscope, Leaf, ShieldCheck, FlaskConical } from "lucide-react";
import { Button } from "../components/ui/button";
import SearchBar from "../components/SearchBar";
import DoctorsNearYou from "../components/DoctorsNearYou";
import { useSettings } from "../contexts/SettingsContext";
import { formatINR } from "../lib/api";

export default function Home() {
    const { settings } = useSettings();
    const [categories, setCategories] = useState([]);
    const [featured, setFeatured] = useState([]);

    useEffect(() => {
        api.get("/categories").then(r => setCategories(r.data)).catch(() => {});
        api.get("/products", { params: { featured: true } }).then(r => setFeatured(r.data.slice(0, 4))).catch(() => {});
    }, []);

    return (
        <div data-testid="home-page">
            {/* HERO */}
            <section className="relative overflow-hidden border-b border-stone-200">
                <div className="absolute inset-0 paper opacity-40" />
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-32 grid lg:grid-cols-12 gap-12 items-center relative">
                    <div className="lg:col-span-7 space-y-8 animate-fade-up">
                        <div className="label-eyebrow text-sky-700">{settings.hero_eyebrow}</div>
                        <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[0.95] text-stone-900">
                            {settings.hero_title_line1}<br />
                            <span className="italic text-sky-700">{settings.hero_title_line2}</span> {settings.hero_title_line3?.startsWith("for") ? "" : <br />}{settings.hero_title_line3}
                        </h1>
                        <p className="text-base sm:text-lg text-stone-600 max-w-xl leading-relaxed">
                            {settings.hero_subtitle}
                        </p>
                        <div className="max-w-xl"><SearchBar /></div>
                        <div className="flex flex-wrap gap-4">
                            <Link to="/ai-doctor"><Button className="rounded-none bg-sky-700 hover:bg-sky-800 px-7 py-6 text-stone-50" data-testid="hero-cta-ai">
                                <Sparkles className="w-4 h-4 mr-2" /> Talk to the AI Doctor
                            </Button></Link>
                            <Link to="/shop"><Button variant="outline" className="rounded-none border-stone-400 text-stone-900 px-7 py-6" data-testid="hero-cta-shop">
                                Browse the Pharmacy <ArrowRight className="w-4 h-4 ml-2" />
                            </Button></Link>
                        </div>
                        <div className="flex flex-wrap gap-x-8 gap-y-3 pt-6 text-xs text-stone-500">
                            <span className="flex items-center gap-2"><Leaf className="w-3.5 h-3.5 text-sky-700" /> Plant-derived remedies</span>
                            <span className="flex items-center gap-2"><FlaskConical className="w-3.5 h-3.5 text-sky-700" /> GMP-certified lab</span>
                            <span className="flex items-center gap-2"><ShieldCheck className="w-3.5 h-3.5 text-sky-700" /> Pharmacist verified</span>
                        </div>
                    </div>
                    <div className="lg:col-span-5 relative">
                        <div className="aspect-[4/5] overflow-hidden border border-stone-200 bg-stone-100">
                            {settings.hero_image_url ? (
                                <img src={settings.hero_image_url} alt="Hero" className="w-full h-full object-cover" loading="eager" />
                            ) : (
                                <div className="w-full h-full grid place-items-center text-stone-400 text-sm">Add hero image in admin</div>
                            )}
                        </div>
                        <div className="absolute -bottom-6 -left-6 bg-stone-50 border border-stone-200 p-4 max-w-[220px] hidden sm:block">
                            <div className="label-eyebrow text-stone-500">{settings.hero_image_caption_label}</div>
                            <div className="font-serif text-2xl text-stone-900 mt-1">{settings.hero_image_caption_value}</div>
                            <div className="text-xs text-stone-600 mt-1">{settings.hero_image_caption_note}</div>
                        </div>
                    </div>
                </div>
            </section>

            {/* FEATURE STRIP */}
            <section className="border-b border-stone-200">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid sm:grid-cols-3 gap-8">
                    <Feature icon={<Sparkles className="w-5 h-5" />} title="AI Symptom Checker" desc="Describe what you feel. Get classical remedy suggestions instantly." to="/ai-doctor" />
                    <Feature icon={<Stethoscope className="w-5 h-5" />} title="Verified Homeopaths" desc="Online video calls or visit a doctor near you." to="/doctors" />
                    <Feature icon={<Leaf className="w-5 h-5" />} title="Authentic Dilutions" desc="Single remedies, mother tinctures, biochemics & more." to="/shop" />
                </div>
            </section>

            {/* DOCTORS NEAR YOU (auto-detected city) */}
            <DoctorsNearYou />

            {/* CATEGORIES */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
                <div className="flex items-end justify-between mb-12">
                    <div>
                        <div className="label-eyebrow text-sky-700">The collection</div>
                        <h2 className="font-serif text-4xl sm:text-5xl text-stone-900 mt-2">Browse by category</h2>
                    </div>
                    <Link to="/shop" className="text-sm text-stone-700 hover:text-sky-700 hidden sm:flex items-center gap-1">View all <ArrowRight className="w-3.5 h-3.5" /></Link>
                </div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                    {categories.slice(0, 8).map((c) => (
                        <Link key={c.id} to={`/shop?category=${c.id}`} className="group border border-stone-200 bg-white p-6 hover:border-sky-700 transition-colors" data-testid={`cat-card-${c.slug}`}>
                            <div className="label-eyebrow text-stone-400">Category</div>
                            <h3 className="font-serif text-2xl text-stone-900 mt-2 group-hover:text-sky-700">{c.name}</h3>
                            <p className="text-xs text-stone-500 mt-2 line-clamp-2">{c.description}</p>
                            <span className="inline-flex items-center gap-1 text-xs text-sky-700 mt-4">Explore <ArrowRight className="w-3 h-3" /></span>
                        </Link>
                    ))}
                </div>
            </section>

            {/* FEATURED */}
            {featured.length > 0 && (
                <section className="border-t border-stone-200 bg-stone-100">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
                        <div className="label-eyebrow text-sky-700">Bestselling remedies</div>
                        <h2 className="font-serif text-4xl sm:text-5xl text-stone-900 mt-2 mb-12">Featured this season</h2>
                        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                            {featured.map((p) => (
                                <Link key={p.id} to={`/product/${p.slug}`} className="bg-white border border-stone-200 group" data-testid={`featured-${p.slug}`}>
                                    <div className="aspect-square bg-stone-100 overflow-hidden">
                                        {p.images?.[0] ? (
                                            <img src={p.images[0]} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                                        ) : (
                                            <div className="w-full h-full grid place-items-center text-stone-400 text-xs">No image</div>
                                        )}
                                    </div>
                                    <div className="p-4">
                                        <h3 className="font-serif text-lg text-stone-900">{p.name}</h3>
                                        {p.variants?.[0] && <div className="text-sm text-sky-700 mt-1">From {formatINR(p.variants[0].price)}</div>}
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* AI DOCTOR PROMO */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 grid lg:grid-cols-2 gap-16 items-center">
                <div className="aspect-[5/4] overflow-hidden border border-stone-200 bg-stone-100">
                    {settings.ai_promo_image_url ? (
                        <img src={settings.ai_promo_image_url} alt="AI Doctor" className="w-full h-full object-cover" />
                    ) : (
                        <div className="w-full h-full grid place-items-center text-stone-400 text-sm">Add AI promo image in admin</div>
                    )}
                </div>
                <div className="space-y-6">
                    <div className="label-eyebrow text-sky-700">{settings.ai_promo_eyebrow}</div>
                    <h2 className="font-serif text-4xl sm:text-5xl text-stone-900">{settings.ai_promo_title}</h2>
                    <p className="text-stone-600 leading-relaxed max-w-lg">
                        {settings.ai_promo_subtitle}
                    </p>
                    <Link to="/ai-doctor"><Button className="rounded-none bg-sky-700 hover:bg-sky-800 px-7 py-6 text-stone-50" data-testid="ai-promo-cta">
                        Start a free consult <ArrowRight className="w-4 h-4 ml-2" />
                    </Button></Link>
                </div>
            </section>
        </div>
    );
}

const Feature = ({ icon, title, desc, to }) => (
    <Link to={to} className="flex items-start gap-4 group">
        <span className="w-10 h-10 border border-stone-300 grid place-items-center text-sky-700 group-hover:bg-sky-700 group-hover:text-stone-50 transition-colors">{icon}</span>
        <div>
            <div className="font-serif text-xl text-stone-900">{title}</div>
            <div className="text-xs text-stone-600 mt-1">{desc}</div>
        </div>
    </Link>
);
